/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

/*
    Hello there! This is a demonstration of how to override a file from the base template.

    It's necessary that the module export interface remain consistent,
    as other files in the base template rely on constants.js, thus we
    import the underlying constants.js, modifies it and re-export it.
*/
import {defineMessage} from 'react-intl'

// Constants used in the used for product searching.
import {
    DEFAULT_LIMIT_VALUES,
    DEFAULT_SEARCH_PARAMS
} from '@salesforce/retail-react-app/app/constants'

DEFAULT_SEARCH_PARAMS.limit = 72
export {DEFAULT_LIMIT_VALUES, DEFAULT_SEARCH_PARAMS}
export const DEFAULT_SKELETON_COUNT = 3
export const PAYMENT_SKELETON_COUNT = 1
export const DEFAULT_MAX_ORDER_QUANTITY = 10
export const ACTIVE_DATA_ENABLED = true
export const CUSTOM_HOME_TITLE = '🎉 Hello Extensible React Template!'

// const used for page designer home page
export const PD_HOMEPAGE_ID = 'homepage'

// stripe secret
export const TEST_STRIPE_SECRET_KEY =
    'sk_test_51No8dmKy0AJRVXWGauTkRgNeU08ffV4QTOJZVW3aa7JnBbATd1IheNnxKWm70Djq9B2eW9m6GPya6n2ZFzATjuGM00O7pVIf6c'

export const STRIPE_SECRET_KEY =
    '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'

export const DEFAULT_SITE_TITLE = 'Explore the Ultimate Anime & Manga Shop | Crunchyroll Store'
export const GC_PAGE_TITLE = 'Gift Card Purchase | Crunchyroll store'
export const GC_PAGE_DESCRIPTION =
    'Discover the latest anime releases & pre-orders at the Official Crunchyroll Store. Shop a variety of figures, clothing, and more. Explore now!'
// Constants
export const PROMO_BANNER_DESKTOP_PAGE_ID = 'instagram-promo-banner-desktop'
export const PROMO_BANNER_MOBILE_PAGE_ID = 'instagram-promo-banner-mobile'
export const OUT_OF_STOCK = 'OUT_OF_STOCK'
export const UNFULFILLABLE = 'UNFULFILLABLE'

// Countries for Store Locator
export const SUPPORTED_STORE_LOCATOR_COUNTRIES = [
    {
        countryCode: 'US',
        countryName: defineMessage({
            defaultMessage: 'United States',
            id: 'store_locator.dropdown.united_states'
        })
    },
    {
        countryCode: 'CA',
        countryName: defineMessage({
            defaultMessage: 'Canada',
            id: 'store_locator.dropdown.canada'
        })
    }
]

export const EMAIL_REGEX =
    /^(?!.*\.\.)([a-zA-Z0-9]+([._%+-]?[a-zA-Z0-9]+)*)@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

export const HTML_REGEX = /^\/([^/]+)\.html(?:\?.*)?$/

export const TOAST_MESSAGE_ADDED_TO_GUEST_WISHLIST = defineMessage({
    id: 'guest_wislist.added_to_wishlist',
    defaultMessage: 'The product has been added to your wishlist.'
})

export const PLACEHOLDER_DATE = '12/31/9999'

export const No_Saved_Payment = defineMessage({
    defaultMessage: 'No Payment Saved',
    id: 'account_payment.info.no_saved_payment'
})

export const No_Saved_Payment_Text = defineMessage({
    defaultMessage: 'Add a payment for faster checkout',
    id: 'account_payment.info.no_saved_payment_text'
})

export const Add_Payment = defineMessage({
    defaultMessage: 'Add Payment',
    id: 'account_payment.info.add_payment'
})

export const Successfully_Removed_Payment = defineMessage({
    defaultMessage: 'Card Removed Successfully',
    id: 'account_payment.info.payment_removed'
})

export const Error_Removing_Payment = defineMessage({
    defaultMessage: 'Error On Card Removal',
    id: 'account_payment.info.payment_removed-Error'
})

export const Successfully_Attached_Payment = defineMessage({
    id: 'account_payment.info.payment_attached',
    defaultMessage: 'Added Card Successfully'
})

export const Error_Attaching_Payment = defineMessage({
    id: 'account_payment.info.payment_attaching_Error',
    defaultMessage: 'Error While Saving Card'
})

export const Add_New_Payment = defineMessage({
    defaultMessage: 'Add New Payment',
    id: 'add_new_payment.button.add_payment'
})

export const Cancel_New_Card = defineMessage({
    defaultMessage: 'Cancel New Payment',
    id: 'cancel_new_payment.button.cancel_payment'
})

export const successfullyAddedAddress = defineMessage({
    defaultMessage: 'New address saved',
    id: 'account_addresses.info.new_address_saved'
})

export const successfullyUpdatedAddress = defineMessage({
    defaultMessage: 'Address updated',
    id: 'account_addresses.info.address_updated'
})

export const successfullyRemovedAddress = defineMessage({
    defaultMessage: 'Address removed',
    id: 'account_addresses.info.address_removed'
})

export const buyNowShipTo = defineMessage({
    defaultMessage: 'Ship To :',
    id: 'pdp_buynow.info.ship_to'
})

export const payWith = defineMessage({
    defaultMessage: 'Pay With :',
    id: 'pdp_buynow.info.card_details'
})

export const total = defineMessage({
    defaultMessage: 'Total :',
    id: 'pdp_buynow.total'
})

export const productAddedSuccessfully = defineMessage({
    defaultMessage: 'Product Added to Cart',
    id: 'account_addresses.info.address_updated'
})

export const privacyPolicies = defineMessage({
    defaultMessage:
        'By Placing your order, you agree to Providio privacy policies and conditions of use',
    id: 'pdp_buynow.privacy_policies'
})

export const butNowProcessError = defineMessage({
    defaultMessage: 'There was an error processing your request',
    id: 'pdp_buynow.process_error'
})

export const defaultFormTitleAriaLabel = defineMessage({
    defaultMessage: 'Address Form',
    id: 'use_address_fields.label.address_form'
})

export const shippingAddressAriaLabel = defineMessage({
    defaultMessage: 'Shipping Address Form',
    id: 'shipping_address.label.shipping_address_form'
})

export const freeLabel = defineMessage({
    defaultMessage: 'Free',
    id: 'checkout_confirmation.label.free'
})

export const GC_ADDED_TO_CART = defineMessage({
    defaultMessage: 'Gift Card added to Cart Successfully',
    id: 'global.success.gc_added_to_cart'
})

export const PRODUCT_ADDED_TO_CART = defineMessage({
    id: 'global.success.product_added_to_cart',
    defaultMessage: 'Product added to cart'
})

export const GC_Creation_Error = defineMessage({
    defaultMessage: 'Error while creating Gift Certificate',
    id: 'global.error.create_gc'
})

export const SFMC_Subscription_Success = defineMessage({
    defaultMessage: 'Email Signup successful',
    id: 'sfmc.suscribed.sucess'
})
export const TOAST_MESSAGE_ADDED_TO_CART = {
    id: 'toast.addedToCart',
    defaultMessage: 'Product added to cart'
}
export const WISHLIST_NAME_EXIST = {
    id: 'toast.wishlistName',
    defaultMessage: 'List name already exists'
}
export const WISHLIST_CREATED = {
    id: 'toast.wishlistCreated',
    defaultMessage: 'Wishlist Created'
}
export const WISHLIST_ERROR = {
    id: 'toast.wishlistError',
    defaultMessage: 'Error creating wishlist'
}
export const WISHLIST_UPDATED = {
    id: 'toast.wishlistUpdated',
    defaultMessage: 'Wishlist Updated'
}

export const UNCONFIRMED_DATE = '12/31/9999'

export const STORAGE_KEY = 'cookie-consent-shown'

export const COOKIE_NAMES = {
    onetrust: 'OptanonConsent'
}

export const PREMIUM_URL = 'https://www.crunchyroll.com/premium/'

export const SHOPPER_CONTEXT_FIELD_TYPES = {
    INT: 'int',
    DOUBLE: 'double',
    ARRAY: 'array'
}
export const SHOPPER_CONTEXT_SEARCH_PARAMS = {
    sourceCode: {paramName: 'src'},
    geoLocation: {
        city: {paramName: 'city'},
        country: {paramName: 'country'},
        countryCode: {paramName: 'countryCode'},
        latitude: {paramName: 'latitude', type: SHOPPER_CONTEXT_FIELD_TYPES.DOUBLE},
        longitude: {paramName: 'longitude', type: SHOPPER_CONTEXT_FIELD_TYPES.DOUBLE},
        metroCode: {paramName: 'metroCode'},
        postalCode: {paramName: 'postalCode'},
        region: {paramName: 'region'},
        regionCode: {paramName: 'regionCode'}
    },
    customQualifiers: {
        // Add custom qualifiers here
    },
    assignmentQualifiers: {
        // Add assignment qualifiers here
    }
}

export * from '@salesforce/retail-react-app/app/constants'
